/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import { get } from 'lodash';
import qs from 'query-string';
import React from 'react';
import { history, replace } from 'redux-first-router';
import urljoin from 'url-join';
import useTheme from '@mui/styles/useTheme';
import { Typography } from '@mui/material';
import { stripHtml } from 'string-strip-html';

import { MAP_BASE_URL } from '../config';

/**
 * Route to target URL in case of success/failure.
 *
 * Returns false when it fell back to a full page load: the document is about to
 * be replaced, so callers must not carry on as though the app is still alive.
 */

export const routeToPage = ({
  basePath,
  targetPath,
}: {
  basePath?: string;
  targetPath?: string;
}): boolean => {
  const base = basePath ? basePath : MAP_BASE_URL;
  const target = targetPath ? urljoin(base, targetPath) : base;

  // The router's history only exists once the lazily-loaded store has mounted.
  // Callers that run before that (the auth0 bootstrap) still need to navigate.
  if (history()) {
    replace(target);
    return true;
  }

  window.location.replace(target);
  return false;
};

/**
 * Current location as a login targetUrl - path, query and hash, no origin.
 */
export const currentPath = (): string =>
  window.location.href.replace(window.location.origin, '');

interface Hint {
  title: string;
  content: string;
}

export const parseHintBold = (text: string = '') => {
  const theme = useTheme();

  return text.split(/(?:{{(.+?)}})/).map((str, index) => {
    return index % 2 === 1 ? (
      <Typography
        key={index}
        variant="body1"
        sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
        component="b"
      >
        {str}
      </Typography>
    ) : (
      str
    );
  });
};

export const parseDescriptionHintBold = (hints: Hint[] = []) => {
  const theme = useTheme();

  return hints.map((hint, hintIndex) => {
    return (
      <React.Fragment key={`hint-${hintIndex}`}>
        {hint.title !== '' && (
          <Typography
            key={`title-${hintIndex}`}
            variant="body1"
            sx={{ fontWeight: 'bold' }}
            component="b"
          >{`${hint.title}: `}</Typography>
        )}
        {stripHtml(hint.content)
          .result.split(/(?:{{(.+?)}})/)
          .map((str, index) => {
            return index % 2 === 1 ? (
              <Typography
                key={index}
                variant="body1"
                sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
                component="b"
              >
                {str}
              </Typography>
            ) : (
              str
            );
          })}
        {<br />}
      </React.Fragment>
    );
  });
};

export const slashFlip = (text: string = '') =>
  text.indexOf('/') > 0 ? `${text.split('/')[1]} (${text.split('/')[0]})` : text;

export const getUrlQueryParams = () => {
  return qs.parse(windowPropertySSR('location.search', ''));
};

/**
 * Load "browser globals" like window (during SSR).
 * See: https://www.gatsbyjs.com/docs/debugging-html-builds/#how-to-check-if-window-is-defined
 */
export const windowPropertySSR = (path?: string, fallback: any = null) =>
  typeof window !== 'undefined' ? (path ? get(window, path, fallback) : window) : fallback;

/**
 * Returns `true` if we are rendering client-side, `false` when we are rendering
 * on the server.
 */
export const isBrowser = () => typeof window !== 'undefined';
